import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Phone, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import PoojaCard from "@/components/PoojaCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import { getGuidance, getPoojas } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { waLink, generalWaMessage } from "@/lib/whatsapp";

const GuidanceDetail = () => {
  const { slug } = useParams();
  const site = useSite();
  const { data: g, isLoading, isError } = useQuery({ queryKey: ["guidance", slug], queryFn: () => getGuidance(slug) });
  const { data: allPoojas = [] } = useQuery({ queryKey: ["poojas", "all"], queryFn: () => getPoojas() });

  if (isLoading) return <Layout><div className="mx-auto max-w-7xl px-6 py-24 text-muted-foreground">Loading…</div></Layout>;
  if (isError || !g) return <Layout><div className="mx-auto max-w-7xl px-6 py-24"><p>Not found.</p><Link to="/guidance" className="text-primary">Back</Link></div></Layout>;

  const recommended = (g.recommended_pooja_slugs || []).map((s) => allPoojas.find((p) => p.slug === s)).filter(Boolean);

  return (
    <Layout>
      <Seo title={g.meta_title || g.title} description={g.meta_description || g.problem_description} />
      <section className="bg-secondary text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 lg:px-12 py-16">
          <Link to="/guidance" className="inline-flex items-center gap-1.5 text-white/80 text-sm hover:text-gold mb-4"><ArrowLeft className="h-4 w-4" /> All Guidance</Link>
          <h1 className="font-heading text-4xl sm:text-5xl text-white tracking-tight">{g.title}</h1>
          <p className="mt-4 text-white/85 text-lg">{g.problem_description}</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-8 lg:px-12 py-14">
        <p className="text-lg leading-relaxed text-muted-foreground whitespace-pre-line">{g.content}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <EnquiryDialog trigger={<button data-testid="guidance-enquire-button" className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white hover:bg-[#CC5204]">Get personal guidance <ArrowRight className="h-4 w-4" /></button>} />
          <a href={waLink(site.whatsapp_number, generalWaMessage)} target="_blank" rel="nofollow noopener noreferrer" data-testid="guidance-whatsapp-button" className="inline-flex items-center gap-2 rounded-md bg-whatsapp px-6 py-3 font-semibold text-white hover:opacity-90"><Phone className="h-4 w-4" /> Ask on WhatsApp</a>
        </div>
      </section>

      {recommended.length > 0 && (
        <section className="bg-cream">
          <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16">
            <h2 className="font-heading text-3xl text-secondary mb-8">Recommended Poojas</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recommended.map((p, i) => <PoojaCard key={p.id} pooja={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default GuidanceDetail;
